import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import { User } from "../types/permissions";
import { clearAntiforgeryToken } from "../auth/antiforgery";

interface AuthContextType {
  user: User | null;
  setUser: Dispatch<SetStateAction<User | null>>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => {
    try {
      const storedUser = localStorage.getItem("moneylights-user");
      if (storedUser) {
        return JSON.parse(storedUser);
      }
    } catch {
      // ignore storage errors
    }
    return null;
  });
  const lastUserIdRef = useRef<string | null>(user?.id ?? null);

  useEffect(() => {
    const currentUserId = user?.id ?? null;
    if (lastUserIdRef.current !== currentUserId) {
      clearAntiforgeryToken();
      lastUserIdRef.current = currentUserId;
    }
  }, [user?.id]);

  useEffect(() => {
    try {
      if (user) {
        localStorage.setItem("moneylights-user", JSON.stringify(user));
      } else {
        localStorage.removeItem("moneylights-user");
      }
    } catch {
      // ignore storage errors
    }
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
